
import React from 'react';
import { Key, Users, Plus } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TraderAccountCard } from '@/components/accounts/TraderAccountCard';
import { SafeApiConnection, TradingAccount, CTraderProfile } from '@/types/trader';
import { useTranslation } from 'react-i18next';
import { useSharedImageLoader } from '@/hooks/useSharedImageLoader';
import { useIsMobile } from '@/hooks/use-mobile';

interface TradingAccountsSectionProps {
  isLoading: boolean;
  filteredAccounts: Array<{
    tradingAccount: TradingAccount;
    apiConnection: SafeApiConnection;
  }>;
  searchQuery: string;
  onToggleConnection: (apiConnectionId: string, shouldConnect: boolean) => Promise<void>;
  onDeleteApiConnection: (apiConnectionId: string) => Promise<void>;
  profile?: CTraderProfile | null;
  onOpenConnectDialog?: () => void;
}

export function TradingAccountsSection({
  isLoading,
  filteredAccounts,
  searchQuery,
  onToggleConnection,
  onDeleteApiConnection,
  profile,
  onOpenConnectDialog
}: TradingAccountsSectionProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  
  // Preload platform logo if profile is available
  const logoUrl = profile ? getPlatformLogoFromProfile(profile) : '';
  const isImageLoading = useSharedImageLoader(logoUrl ? [logoUrl] : []);
  
  function getPlatformLogoFromProfile(profile: CTraderProfile): string {
    const platformName = (profile.broker || profile.prop_firm || '').toLowerCase();
    
    switch(platformName) {
      case 'coinstrat pro':
        return 'https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//logo%20coinstrat%20pro.png';
      case 'fxpro':
        return 'https://play-lh.googleusercontent.com/z1qgps1oruxuJnPdEJjK_209y-TI2v0bTQeZf8DxKRz2cR0ULY5-YFzKknA3YzlB7g';
      case 'xm':
        return 'https://play-lh.googleusercontent.com/EPrP4-hkROiDGPmLnMSKaFlfWHCI5HdV1EWXXVnrHezxYK74czgVhPOxn-psu5_T6A';
      case 'ic market':
        return 'https://play-lh.googleusercontent.com/S_LUow-89YSbQdIf0cYK2qWLVOLGCKkRkj9PRlBHzWwE80-znbQZs95TH96cCIbhDA';
      case 'ftmo':
        return 'https://play-lh.googleusercontent.com/Zz9gWxJt7C8wPZ1jd-8O9HMWAkH4KO8OfKvwPinpvZPtYs4RcbC1ckwZQPj4HNB8GQ';
      case 'ai prop':
        return '/lovable-uploads/d8c04af7-018a-4e74-8091-9940d7e03ad7.png';
      default:
        return 'https://play-lh.googleusercontent.com/rZ6p-VXduWWqsOBGpwsyK_ZpNVOYgj8bKlqQGqPbtVwsTAfVk67s3cSLxuXctJBPzZU';
    }
  }

  const LoadingCard = () => (
    <Card className={`${isMobile ? 'p-4' : 'p-6'} bg-gradient-to-br from-background to-background/50 border border-border/50`}>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className={`${isMobile ? 'h-10 w-10' : 'h-12 w-12'} rounded-xl`} />
          <div className="space-y-2 flex-1">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-4 w-24" />
          </div>
          <Skeleton className={`${isMobile ? 'h-6 w-16' : 'h-8 w-20'} rounded-full`} />
        </div>
        <div className="flex justify-between items-center">
          <Skeleton className={`${isMobile ? 'h-10 w-28' : 'h-12 w-32'} rounded-xl`} />
          <Skeleton className="h-6 w-16 rounded-full" />
        </div>
      </div>
    </Card>
  );

  const EmptyStateCard = ({ isSearch = false }: { isSearch?: boolean }) => (
    <Card className="col-span-full border-dashed border-2 border-muted bg-gradient-to-br from-muted/20 to-muted/10">
      <div className={`flex flex-col items-center justify-center ${isMobile ? 'py-12 px-4' : 'py-16 px-6'} text-center`}>
        <div className={`${isMobile ? 'mb-4 p-4' : 'mb-6 p-6'} bg-gradient-to-br from-primary/10 to-primary/5 rounded-full`}>
          <Users className={`${isMobile ? 'h-8 w-8' : 'h-10 w-10'} text-primary`} />
        </div>
        <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-foreground mb-3`}>
          {isSearch ? t('no_trading_accounts_search') : t('no_accounts_connected')}
        </h3>
        <p className={`${isMobile ? 'text-sm' : 'text-sm'} text-muted-foreground ${isMobile ? 'mb-6' : 'mb-8'} max-w-md leading-relaxed`}>
          {isSearch 
            ? "Try adjusting your search criteria or clear the search to see all accounts."
            : "Connect your first trading account to start managing your CTrader connections and monitor your trading activity."
          }
        </p>
        {!isSearch && onOpenConnectDialog && (
          <Button 
            onClick={onOpenConnectDialog} 
            size={isMobile ? "sm" : "default"}
            className="group bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02]"
          >
            <Plus className={`${isMobile ? 'h-4 w-4 mr-1' : 'h-5 w-5 mr-2'} transition-transform duration-200 group-hover:rotate-90`} />
            {isMobile ? 'Connect Account' : 'Connect Account'}
          </Button>
        )}
      </div>
    </Card>
  );
  
  return (
    <div className={`space-y-${isMobile ? '4' : '6'}`}>
      <div className="flex items-center gap-3">
        <div className={`${isMobile ? 'p-2' : 'p-3'} bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl`}>
          <Key className={`${isMobile ? "h-5 w-5" : "h-6 w-6"} text-primary`} />
        </div>
        <div>
          <h2 className={`${isMobile ? "text-xl" : "text-2xl"} font-bold text-foreground`}>
            {t('api_keys_and_connections')}
          </h2>
          <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground font-medium`}>
            Manage your trading account connections and API keys
          </p>
        </div>
      </div>

      <Separator className="bg-gradient-to-r from-transparent via-border to-transparent" />

      <div className={`grid grid-cols-1 ${isMobile ? 'gap-3' : 'md:grid-cols-2 lg:grid-cols-3 gap-6'}`}>
        {isLoading || isImageLoading ? (
          <>
            <LoadingCard />
            <LoadingCard />
            <LoadingCard />
          </>
        ) : filteredAccounts.length > 0 ? (
          filteredAccounts.map(({ tradingAccount, apiConnection }) => (
            <TraderAccountCard
              key={tradingAccount.id}
              tradingAccount={tradingAccount}
              apiConnection={apiConnection}
              cspProfile={profile}
              onToggleConnection={onToggleConnection}
              onDelete={onDeleteApiConnection}
            />
          ))
        ) : (
          <EmptyStateCard isSearch={!!searchQuery} />
        )}
      </div>
    </div>
  );
}
